import PartyPackageCard from './PartyPackageCard';
import PartyPackageCardSkeleton from './PartyPackageCardSkeleton';
import hero from "./image/food-img.png";
import './PartyPackageListingPage.css';
import './VenuesListingPage.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useParams } from 'react-router-dom';
import FilterBar from './FilterBar';
import FilterModal from './FilterModel';
import { Helmet } from 'react-helmet-async';

const filters = [
    {
        "label": "Filter",
        "type": "button",
        "icon": "PiSlidersHorizontalBold"
    },
    {
        "label": "Sort By",
        "type": "dropdown",
        "group": "sort",
        "icon": "IoIosArrowDown",
        "options": ["A to Z", "Z to A"]
    },
    {
        "label": "Review",
        "type": "dropdown",
        "icon": "IoIosArrowDown",
        "options": ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"]
    },
    {
        "label": "My Favorite",
        "type": "pill",
        "icon": "FaHeart"
    },
    {
        "label": "Friends Favorite",
        "type": "pill",
        "icon": "FaUsers"
    },
    {
        "label": "Under 999",
        "type": "pill",
        "icon": "FaRupeeSign"
    },
    {
        "label": "Previously Booked",
        "type": "pill",
        "icon": "FaHistory"
    },
    {
        "label": "New to You",
        "type": "pill",
        "icon": "FaStar"
    }
];

export default function VenuesListingPage() {
    const { cityname } = useParams();
    const { pathname } = useLocation();

    // State management
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCitywiseClubs, setLoadingCitywiseClubs] = useState(false);
    const [error, setError] = useState(null);
    const [filtersAndSort, setFiltersAndSort] = useState({});
    const [showFilter, setShowFilter] = useState(false);
    const [location, setLocation] = useState({
        latitude: "",
        longitude: ""
    });
    const [keyword, setKeyword] = useState("");
    const [visibleCount, setVisibleCount] = useState(10);
    const [limit, setLimit] = useState(20);
    const [hasMore, setHasMore] = useState(true);

    const loaderRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Derived values
    const CITY_Name = cityname?.replace(/-/g, " ");
    const segments = pathname.split('/').filter(Boolean);
    const pageSlug = segments[segments.length - 1];
    const pageName = CITY_Name || pageSlug;

    // Utility function to generate club URLs
    const generateClubUrl = useCallback((clubName, cityName, areaName) => {
        const format = (str) => (str ? str.trim().replace(/\s+/g, "_").toLowerCase() : "");
        return `/${format(clubName)}-${format(cityName)}-${format(areaName)}`;
    }, []);

    // API call for city-wise clubs (when cityname param exists)
    const fetchCityWiseClubs = useCallback(async (cityName) => {
        if (!cityName) return;

        setLoading(true);
        setLoadingCitywiseClubs(true);
        setError(null);

        try {
            // Cancel previous request if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            const formData = new FormData();
            formData.append("city_id", cityName);
            formData.append("platform", "web");

            const response = await axios.post(
                "https://partywitty.com/master/APIs/Common/cityWiseClub",
                formData,
            );

            const { data: responseData, status, msg } = response.data;

            if (status === true && responseData) {
                setData(responseData);
                setHasMore(responseData.length >= limit);
            } else {
                setData([]);
                setHasMore(false);
                if (msg) {
                    setError(msg);
                }
            }
        } catch (error) {
            if (error.name !== 'CanceledError') {
                console.error("Error fetching city-wise clubs: ", error);
                setError("Failed to load venues. Please try again.");
                setData([]);
            }
        } finally {
            setLoading(false);
            setLoadingCitywiseClubs(false);
        }
    }, [limit]);

    // API call for all clubs (when no cityname param or using filters)
    const fetchAllClubs = useCallback(async (
        latitude,
        longitude,
        selectedVenueIds = [],
        packageIds = [],
        searchKeyword = ""
    ) => {
        if (!latitude || !longitude) return;

        setLoading(true);
        setError(null);

        try {
            // Cancel previous request if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            const formData = new FormData();
            formData.append("latitude", latitude);
            formData.append("longitude", longitude);
            formData.append("venue_id", selectedVenueIds.join(","));
            formData.append("limit", limit.toString());
            formData.append("title_id", packageIds.join(","));
            formData.append("keyword", searchKeyword);

            const response = await axios.post(
                "https://partywitty.com/master/APIs/Common/allClubNew",
                formData,
            );

            const { data: responseData, status } = response.data;

            if (status === true && responseData) {
                setData(responseData);
                setHasMore(responseData.length >= limit);
            } else {
                setData([]);
                setHasMore(false);
            }
        } catch (error) {
            if (error.name !== 'CanceledError') {
                console.error("Error fetching all clubs: ", error);
                setError("Failed to load venues. Please try again.");
                setData([]);
            }
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Get user's current location
    const getCurrentLocation = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!("geolocation" in navigator)) {
                reject(new Error("Geolocation is not available in this browser."));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    resolve({ latitude: latitude.toString(), longitude: longitude.toString() });
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000 // 5 minutes
                }
            );
        });
    }, []);

    // Main data fetching function
    const fetchData = useCallback(async () => {
        try {
            // If cityname parameter exists, fetch city-wise data
            if (CITY_Name) {
                await fetchCityWiseClubs(CITY_Name);
            } else {
                // Otherwise, get location and fetch all clubs
                const locationData = await getCurrentLocation();
                setLocation(locationData);

                const venueIds = filtersAndSort?.venue || [];
                const packageIds = filtersAndSort?.package || [];

                await fetchAllClubs(
                    locationData.latitude,
                    locationData.longitude,
                    venueIds,
                    packageIds,
                    keyword
                );
            }
        } catch (error) {
            console.error("Error in fetchData:", error);
            setError("Failed to load venues. Please try again.");
        }
    }, [CITY_Name, fetchCityWiseClubs, getCurrentLocation, fetchAllClubs, filtersAndSort, keyword]);

    // Fetch data with current filters
    const fetchDataWithFilters = useCallback(async () => {
        if (!CITY_Name && (!location.latitude || !location.longitude)) {
            try {
                const locationData = await getCurrentLocation();
                setLocation(locationData);

                const venueIds = filtersAndSort?.venue || [];
                const packageIds = filtersAndSort?.package || [];

                await fetchAllClubs(
                    locationData.latitude,
                    locationData.longitude,
                    venueIds,
                    packageIds,
                    keyword
                );
            } catch (error) {
                console.error("Error getting location for filters:", error);
                setError("Please enable location access to use filters.");
            }
        } else if (!CITY_Name) {
            const venueIds = filtersAndSort?.venue || [];
            const packageIds = filtersAndSort?.package || [];

            await fetchAllClubs(
                location.latitude,
                location.longitude,
                venueIds,
                packageIds,
                keyword
            );
        }
    }, [CITY_Name, location, getCurrentLocation, fetchAllClubs, filtersAndSort, keyword]);

    // Filter and sort data
    const filteredData = data?.filter((item) => {
        // Review filter
        if (filtersAndSort.selectedDropdowns?.Review) {
            const selectedReview = parseInt(filtersAndSort.selectedDropdowns.Review[0]);
            const itemRating = parseFloat(item.totalAverageRating);

            if (isNaN(itemRating)) return false;

            const lowerBound = selectedReview - 0.4;
            const upperBound = selectedReview + 0.5;

            if (itemRating < lowerBound || itemRating >= upperBound) {
                return false;
            }
        }

        // Add other filter logic here as needed
        // if (filtersAndSort.selectedPills?.includes("Friends Favorite") && !item.isFriendFavorite) {
        //     return false;
        // }

        // if (filtersAndSort.selectedPills?.includes("Under 999") && item.price >= 999) {
        //     return false;
        // }

        return true;
    }).sort((a, b) => {
        const sortType = filtersAndSort.selectedDropdowns?.["Sort By"];
        if (sortType === "A to Z") return a.name.localeCompare(b.name);
        if (sortType === "Z to A") return b.name.localeCompare(a.name);
        return 0;
    });

    // Intersection Observer for infinite scroll
    const handleObserver = useCallback(
        (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && !loading && hasMore) {
                setVisibleCount((prev) => prev + 10);
                setLimit((prev) => prev + 20);
            }
        },
        [loading, hasMore]
    );

    // Effects
    useEffect(() => {
        fetchData();

        // Cleanup function
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchData]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '20px',
            threshold: 0.1,
        });

        const target = loaderRef.current;
        if (target) observer.observe(target);

        return () => {
            if (target) observer.unobserve(target);
        };
    }, [handleObserver]);

    // Refetch when limit changes (for infinite scroll)
    useEffect(() => {
        if (limit > 20) { // Only refetch if limit has increased
            fetchData();
        }
    }, [limit, fetchData]);

    return (
        <>
            <Helmet>
                <title>
                    {pageName ? `${pageName} - ` : ""}Instant Table Booking – Deals on Clubs & Restaurants Nearby
                </title>
                <meta
                    name="description"
                    content={`Reserve tables online at top party venues${pageName ? ` in ${pageName}` : " in Delhi-NCR"}. Get amazing offers on clubs, cafes, and restaurants with PartyWitty's easy booking.`}
                />
            </Helmet>

            <div className='party-package-section'>
                <div className='party-package-list'>
                    <div className="partypackage-hero-container">
                        <div className="partypackage-image-wrapper">
                            <img
                                src={hero}
                                alt="Table booking at nearby clubs and restaurants with instant deals"
                                className="partypackage-image"
                                loading="lazy"
                            />
                            <div className="partypackage-overlay">
                                <div className="partypackage-title">
                                    <h1>{pageName}</h1>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='party-package-page-filter-container'>
                        <div className='party-package-page-filter'>
                            <div className='party-package-page-filter-title-box'>
                                <div className='pppftb-filter'>
                                    <FilterBar
                                        filterList={filters}
                                        setFiltersAndSort={setFiltersAndSort}
                                        setShowFilter={setShowFilter}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            <p>{error}</p>
                            <button onClick={fetchData}>Retry</button>
                        </div>
                    )}

                    <div className='party-package-page-container'>
                        {loading && data.length === 0 ? (
                            Array.from({ length: 6 }).map((_, index) => (
                                <PartyPackageCardSkeleton key={index} />
                            ))
                        ) : (
                            filteredData.slice(0, visibleCount).map((item) => (
                                <Link
                                    to={generateClubUrl(item.name, item.cityName, item.areaName)}
                                    key={item.id}
                                >
                                    <PartyPackageCard
                                        data={item}
                                        isListingPage={true}
                                        isVenueList={true}
                                    />
                                </Link>
                            ))
                        )}

                        {hasMore && <div ref={loaderRef} style={{ height: "1px" }} />}

                        {loading && data.length > 0 && (
                            <p className="venue-loader-text">Loading more venues...</p>
                        )}
                        {loadingCitywiseClubs && (
                            <p className="venue-loader-text">Loading venues...</p>
                        )}

                        {!loading && !hasMore && data.length > 0 && (
                            <p className="venue-loader-text">No more venues to load</p>
                        )}

                        {!loading && data.length === 0 && !error && (
                            <p className="venue-loader-text">No venues found</p>
                        )}
                    </div>
                </div>

                <div className='venue-page-design-bottom-footer-tags-section'>
                    <div className='venue-page-detail-page-design-bottom-footer-tags'>
                        <div className='venue-page-detail-page-design-bottom-footer-tags-text'>
                            <h1>Instantly Book Tables at Top Lounges, Bars & Cafés Near You</h1>
                            <p>Looking for the perfect spot for a spontaneous night out or planned party? Instantly book tables at the best clubs, cafés, bars, and lounges near you with PartyWitty. Whether it's for a birthday, date night, or group hangout, explore top-rated venues across Delhi NCR. Check real-time availability, compare table offers, and reserve in just a few clicks. Enjoy exclusive discounts and hassle-free bookings at restaurants and clubs that match your vibe. Make every outing smooth and special — only with PartyWitty.</p>
                        </div>
                        <div className='venue-page-detail-page-design-bottom-footer-tags-text'>
                            <h1>Book VIP Tables in Delhi NCR with Exclusive Deals!</h1>
                            <p>Skip the wait and grab the best seats in town! PartyWitty lets you book tables instantly at top clubs, lounges, and restaurants near you. Whether you're planning a dinner date, birthday celebration, or just a night out with friends, find verified venues across Delhi NCR. Access live availability, compare offers, and unlock up to 50% off on your booking. From rooftop views to dance floors and BYOB cafés — book your ideal spot in minutes with PartyWitty.</p>
                        </div>
                        <div className='venue-page-detail-page-design-bottom-footer-tags-text'>
                            <h1>Reserve Tables with Custom Packages & Party Deals Instantly</h1>
                            <p>Why wait? Instantly reserve your table and unlock custom party deals with PartyWitty. From casual dinners to lively club nights, find the perfect place with the best offers near you. Choose from top restaurants, pubs, and lounges in Delhi, Noida, and Gurgaon — all offering personalized packages for birthdays, group parties, or corporate events. Check availability in real-time, pick your food and drink combos, and book your celebration spot in just a few taps.</p>
                        </div>
                    </div>
                </div>

                <FilterModal
                    isOpen={showFilter}
                    onClose={() => setShowFilter(false)}
                    setFiltersAndSort={setFiltersAndSort}
                    fetchDataWithFilters={fetchDataWithFilters}
                />
            </div>
        </>
    );
}